import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Alert,
  Modal,
  Form,
  Input,
  Popconfirm,
  message,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { getTokens } from '../../services/authApi';

const { Text } = Typography;
const { TextArea } = Input;

const API_BASE = '/api/owner/hub';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface CorrectionRecord {
  id: number;
  proposed_by_email: string;
  target_table: string;
  target_id: number | null;
  field_name: string;
  current_value: string | null;
  proposed_value: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_notes: string | null;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const tokens = getTokens();
  const accessToken = tokens?.accessToken ?? null;
  return fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(options.headers ?? {}),
    },
  });
}

function tableTag(table: string) {
  const colorMap: Record<string, string> = {
    labor_times: 'blue',
    learned_labor_times: 'purple',
    parts_saved_templates: 'cyan',
    vehicles: 'green',
    customers: 'orange',
  };
  return <Tag color={colorMap[table] ?? 'default'}>{table.replace(/_/g, ' ')}</Tag>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Reject Modal
// ─────────────────────────────────────────────────────────────────────────────

interface RejectModalProps {
  open: boolean;
  correctionId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RejectModal: React.FC<RejectModalProps> = ({ open, correctionId, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (!correctionId) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_BASE}/corrections/${correctionId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ rejection_notes: values.rejection_notes }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      messageApi.success('Correction rejected');
      form.resetFields();
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to reject correction';
      messageApi.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Reject Correction"
        open={open}
        onCancel={onClose}
        onOk={handleSubmit}
        okText="Reject"
        okButtonProps={{ danger: true, loading: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rejection_notes"
            label="Rejection Notes"
            rules={[{ required: true, message: 'Please provide a reason for rejection' }]}
          >
            <TextArea
              rows={4}
              placeholder="Explain why this correction is being rejected..."
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const CorrectionQueue: React.FC = () => {
  const [rows, setRows] = useState<CorrectionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actioning, setActioning] = useState<number | null>(null);
  const [rejectModalId, setRejectModalId] = useState<number | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/corrections`);
      if (!res.ok) {
        if (res.status === 404) {
          setRows([]);
          return;
        }
        throw new Error(`Server returned ${res.status}`);
      }
      const data = await res.json();
      setRows(data.corrections ?? data.data ?? data ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id: number) => {
    setActioning(id);
    try {
      const res = await apiFetch(`${API_BASE}/corrections/${id}/approve`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      messageApi.success('Correction approved and applied');
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to approve correction';
      messageApi.error(msg);
    } finally {
      setActioning(null);
    }
  };

  const columns: ColumnsType<CorrectionRecord> = [
    {
      title: 'Proposed By',
      dataIndex: 'proposed_by_email',
      key: 'proposed_by_email',
      width: 180,
      ellipsis: true,
      render: (val: string) => <Text style={{ fontSize: 13 }}>{val}</Text>,
    },
    {
      title: 'Target Table',
      dataIndex: 'target_table',
      key: 'target_table',
      width: 160,
      render: (val: string) => tableTag(val),
    },
    {
      title: 'Field',
      dataIndex: 'field_name',
      key: 'field_name',
      width: 140,
      render: (val: string) => <Text code>{val}</Text>,
    },
    {
      title: 'Change',
      key: 'change',
      ellipsis: true,
      render: (_: unknown, row: CorrectionRecord) => (
        <Space size={6}>
          <Text
            type="secondary"
            style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', fontSize: 12 }}
          >
            {row.current_value ?? '(empty)'}
          </Text>
          <ArrowRightOutlined style={{ color: '#8c8c8c', fontSize: 11 }} />
          <Text
            strong
            style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', fontSize: 12, color: '#1677ff' }}
          >
            {row.proposed_value}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (val: string) => (
        <Tooltip title={val}>
          <Text style={{ fontSize: 13 }}>{val}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (val: CorrectionRecord['status']) => {
        const colorMap: Record<CorrectionRecord['status'], string> = {
          pending: 'orange',
          approved: 'green',
          rejected: 'red',
        };
        return (
          <Tag color={colorMap[val]} style={{ textTransform: 'capitalize' }}>
            {val}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 165,
      align: 'center',
      render: (_: unknown, row: CorrectionRecord) => {
        if (row.status !== 'pending') {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.reviewed_by ?? '—'}
            </Text>
          );
        }
        return (
          <Space size="small">
            <Popconfirm
              title="Approve this correction?"
              description="The proposed value will be applied to the database."
              onConfirm={() => handleApprove(row.id)}
              okText="Approve"
              cancelText="Cancel"
            >
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={actioning === row.id}
                disabled={actioning !== null && actioning !== row.id}
              >
                Approve
              </Button>
            </Popconfirm>
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              disabled={actioning !== null}
              onClick={() => setRejectModalId(row.id)}
            >
              Reject
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {contextHolder}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'flex-end' }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Space>

      {error && (
        <Alert
          type="error"
          message="Failed to load corrections"
          description={error}
          style={{ marginBottom: 16 }}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      <Table<CorrectionRecord>
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        size="middle"
        scroll={{ x: 900 }}
        locale={{ emptyText: 'No pending corrections' }}
        pagination={{ pageSize: 25, showTotal: (total) => `${total} corrections` }}
      />

      <RejectModal
        open={rejectModalId !== null}
        correctionId={rejectModalId}
        onClose={() => setRejectModalId(null)}
        onSuccess={() => {
          setRejectModalId(null);
          load();
        }}
      />
    </div>
  );
};

export default CorrectionQueue;
